import { CustomerRoleName } from '@dtp/membership-service-types/dist/customerInformation/CustomerRoleNameTypes';
import { CustomerMembershipStatus } from '@dtp/membership-service-types';
import { useCallback, useMemo } from 'react';
import { ProductID } from '@lib/ProductID';
import {
  hasCancellableSubscriptionChange,
  householdHasRoadsideDowngrade,
  householdMemberHasCancellableChange,
  householdMemberHasMcDowngrade,
} from '@lib/membershipDowngradeValidation';
import useSelector from '@redux/typedHooks';
import { ServiceStatus } from '../../../types/customerInformationType';

export const useMembershipStatus = () => {
  const customer = useSelector((state) => state.myMembership.customerInformation.data?.customer);
  const membership = useSelector((state) => state.myMembership.customerInformation.data?.membership);
  const households = useSelector((state) => state.myMembership.customerInformation.data?.households || []);
  const services = useSelector((state) => state.myMembership.customerInformation.data?.services);

  // Customers belong to a single household; we always read the first entry. If the data model
  // ever exposes multiple households per customer this assumption will need to be revisited.
  const household = useMemo(() => households[0], [households]);

  const isMainHouseholdMember = useMemo(
    () => household?.customerRoles.includes(CustomerRoleName.MAIN_HOUSEHOLD_MEMBER) || false,
    [household],
  );

  const isHouseholdMember = useMemo(
    () => household?.customerRoles?.includes(CustomerRoleName.HOUSEHOLD_MEMBER) || false,
    [household?.customerRoles],
  );

  const isPayer = useMemo(() => household?.isPayer, [household?.isPayer]);

  const isMembershipActive = useMemo(
    () => membership?.status === CustomerMembershipStatus.ActiveMembership,
    [membership?.status],
  );

  const isMembershipValid = useMemo(() => membership?.isValid, [membership?.isValid]);

  const isMembershipPending = useMemo(
    () => membership?.status === CustomerMembershipStatus.PendingMembership,
    [membership?.status],
  );

  const isMembershipCancelled = useMemo(
    () => membership?.status === CustomerMembershipStatus.CancelledMembership,
    [membership?.status],
  );

  const isMembershipExpired = useMemo(
    () => membership?.status === CustomerMembershipStatus.ExpiredMembership,
    [membership?.status],
  );

  const canAdministrateMembership = useMemo(
    () =>
      customer?.customerRoleName === CustomerRoleName.STANDALONE_MEMBER ||
      customer?.customerRoleName === CustomerRoleName.MAIN_HOUSEHOLD_MEMBER,
    [customer?.customerRoleName],
  );

  const canAddHouseholdMembers = useMemo(
    () => household?.canAddHouseholdMembers === true,
    [household?.canAddHouseholdMembers],
  );

  const canRemoveHouseholdMembers = useMemo(
    () => !!customer && !!household && household.canAddHouseholdMembers === true,
    [customer, household],
  );

  const canAddRoadsideAssistance = useMemo(() => {
    if (!isMembershipActive) return false;

    const raProduct = services?.find((s) => s.product?.id === ProductID.RoadSideAssistance);
    return raProduct?.serviceStatus === ServiceStatus.CanBeAdded;
  }, [isMembershipActive, services]);

  const canRemoveRoadsideAssistance = useMemo(
    () => !!customer && !!household && householdHasRoadsideDowngrade(household),
    [customer, household],
  );

  const canRemoveNafMC = useMemo(
    () => !!customer && !!household && hasCancellableSubscriptionChange(household),
    [customer, household],
  );

  const canTerminateMembership = useMemo(
    () => !!customer && !!household && hasCancellableSubscriptionChange(household),
    [customer, household],
  );

  const canDowngradeHouseholdMember = useCallback(
    (customerId: string) => householdMemberHasCancellableChange(household, customerId),
    [household],
  );

  const canRemoveMemberNafMC = useCallback(
    (customerId: string) => householdMemberHasMcDowngrade(household, customerId),
    [household],
  );

  const hasHouseholdMembers = useMemo(
    () => household?.numberOfPersonsInHousehold && household?.numberOfPersonsInHousehold > 1,
    [household],
  );

  return {
    isMainHouseholdMember,
    isHouseholdMember,
    isPayer,
    isMembershipActive,
    isMembershipValid,
    isMembershipPending,
    isMembershipCancelled,
    isMembershipExpired,
    canAdministrateMembership,
    canAddHouseholdMembers,
    canRemoveHouseholdMembers,
    canAddRoadsideAssistance,
    canRemoveRoadsideAssistance,
    canRemoveNafMC,
    canRemoveMemberNafMC,
    canTerminateMembership,
    canDowngradeHouseholdMember,
    hasHouseholdMembers,
  };
};
